/**
 * Domain check API client + display helpers (sales intel).
 * Matches apps/api/src/modules/ariregister/domain.routes.ts.
 */
import { api } from "./api";

export interface MxRecord {
  priority: number;
  exchange: string;
}

export interface DomainCheck {
  domain: string;
  checkedAt: string;
  /** True when the answer came from the 7-day cache rather than a live lookup. */
  cached: boolean;

  registrar: string | null;
  registrarUrl: string | null;
  /** Domain registration date - the "how long have they been online" signal. */
  registeredAt: string | null;
  expiresAt: string | null;
  registryChangedAt: string | null;
  registrantName: string | null;
  domainStatus: string[];

  nameservers: string[];
  aRecords: string[];
  aaaaRecords: string[];
  mxRecords: MxRecord[];
  txtRecords: string[];
  cname: string | null;
  spf: string | null;
  dmarc: string | null;

  dnsProvider: string | null;
  mailProvider: string | null;

  raw: {
    whois: string | null;
    rdap: unknown;
    dns: unknown;
  };
  /** Partial failure (e.g. WHOIS timed out but DNS answered). */
  error: string | null;
}

/** Check the domain stored on a register entity (`ArEntity.webUrl`). */
export const checkEntityDomain = (regCode: string, refresh = false): Promise<DomainCheck> =>
  api.get<DomainCheck>(
    `/admin/ariregister/${encodeURIComponent(regCode)}/domain${refresh ? "?refresh=1" : ""}`,
  );

/** Check any address (URL or bare host). */
export const checkDomain = (domain: string, refresh = false): Promise<DomainCheck> =>
  api.get<DomainCheck>(
    `/admin/ariregister/domain?domain=${encodeURIComponent(domain)}${refresh ? "&refresh=1" : ""}`,
  );

/** Whole years since a date, for "the site has existed for N years". */
export function yearsSince(iso: string | null): number | null {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  return Math.floor((Date.now() - then.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
}

/** Days until a date; negative when already past. Null when unknown. */
export function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  return Math.ceil((then.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
}

/** Expiry within this many days is worth flagging on a sales call. */
export const EXPIRY_WARN_DAYS = 60;
